<template>
  <BasePanel title="GIS Map"
    style="max-width: 27em; height: 92%; display: flex; flex-direction: column; position: relative; ">
    <!-- Text field -->
    <v-text-field
      v-if="!currentCategory"
      class="flex-none"
      ref="searchField"
      v-model="searchText"
      :loading="loading"
      clearable
      solo
      label="Search nearby places"
      :light="true"
    />

    <!-- Categories -->
    <div
      v-if="(!searchText || searchText.length < 1) && !currentCategory"
      class="categories-container flex"
      style="overflow-y: auto"
    >
      <!-- All categories -->
      <div
        v-for="(categories, i) of categoryRows"
        :key="i"
        class="categories-row-container"
      >
        <CategoryItem
          v-for="(category, j) of categories"
          :key="j"
          class="category-item my-2"
          :image="category.icon"
          color="#3c709c"
          :name="category.name"
          @click="onClickCategory(category)"
        />
      </div>
    </div>

    <!-- List of Points -->
    <div class="flex d-flex flex-column utilities-viewer-container">
      <div v-if="currentCategory" class="flex-none d-flex align-center">
        <!-- Clear Current Category -->
        <v-btn
          class="flex-none"
          flat
          icon
          small
          color="primary"
          @click="clearCurrentCategory"
        >
          <v-icon dark>keyboard_backspace</v-icon>
        </v-btn>

        <!-- Current Category -->
        <CategoryItem
          disabled
          class="ml-1 flex-none"
          horizontal
          :image="currentCategory.icon"
          color="var(--primary)"
          :name="currentCategory.name"
        />
      </div>

      <!-- List of Points -->
      <div
        v-if="(searchText && searchText.length >= 1) || currentCategory"
        class="flex utilities-container"
      >
        <v-progress-linear
          v-if="currentCategory && loading"
          :indeterminate="true"
          :height="3"
          style="margin: 0 !important"
        />

        <template v-if="filteredUtilities">
          <div
            v-for="(point, index) of filteredUtilities"
            :key="index"
            class="point-item-container touchable-highlight-background"
            @click="highlightPoint(point)"
          >
            <div class="point-content-container">
              <div class="point-name-text">{{ point.feature_name }}</div>
              <div class="point-address-text">{{ point.location }}</div>
            </div>

            <div class="point-actions">
              <v-icon
                class="touchable-scale"
                @click.stop="zoomToUtility(point)"
              >
                zoom_in
              </v-icon>
              <v-icon
                class="touchable-scale"
                @click.stop="
                  highlightPoint(point);
                  navigateToUtility(point);
                "
              >
                directions
              </v-icon>
            </div>
          </div>
        </template>

        <div
          v-if="filteredUtilities && filteredUtilities.length === 0 && !loading"
        >
          No result found!
        </div>
      </div>
    </div>
  </BasePanel>
</template>

<script>
import sdk from "@/api/sdk";

import BasePanel from "@/components/BasePanel";
import CategoryItem from "./CategoryItem";

import { chunk } from "@/utils/Array";
import { navigate } from "@/utils/Navigation";

function isPointInBounds(lat, lng, bounds) {
  return (
    lat >= bounds.south &&
    lat <= bounds.north &&
    lng >= bounds.west &&
    lng <= bounds.east
  );
}

export default {
  components: {
    BasePanel,
    CategoryItem
  },

  props: {
    value: {
      type: Array,
      required: true
    },
    visibleOverlayBounds: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      searchText: "",

      utilities: null,

      currentCategory: null,

      categories: [],
      loading: false
    };
  },

  computed: {
    currentUtilities: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      }
    },

    categoryRows() {
      return chunk(this.categories, 3);
    },

    filteredUtilities() {
      if (!this.utilities) return;

      let result = [...this.utilities];

      if (this.visibleOverlayBounds.length > 0) {
        result = result.filter(u =>
          this.visibleOverlayBounds.some(bounds =>
            isPointInBounds(Number(u.latitude), Number(u.longitude), bounds)
          )
        );
      } else {
        return [];
      }

      if (this.searchText && this.searchText.length > 0) {
        result = result.filter(
          c =>
            c.feature_name
              .toLowerCase()
              .indexOf(this.searchText.toLowerCase()) !== -1
        );
      }

      // Category
      if (this.currentCategory) {
        result = result.filter(u => u.category_id === this.currentCategory.id);
      }

      return result;
    }
  },

  watch: {
    filteredUtilities(val) {
      if (!val) return;

      this.currentUtilities = [...val];

      this.highlightPoint(val.length === 1 ? val[0] : undefined);
    }
  },

  async created() {
    await this.fetchCategories();
    await this.fetchUtilities();
  },

  methods: {
    navigateToUtility(utility) {
      navigate({
        destination: {
          latitude: Number(utility.latitude),
          longitude: Number(utility.longitude)
        },
        params: [
          {
            key: "travelmode",
            value: "driving" // may be "walking", "bicycling" or "transit" as well
          },
          {
            key: "dir_action",
            value: "navigate" // this instantly initializes navigation using the given travel mode
          }
        ]
      });
    },

    zoomToUtility(utility) {
      this.$emit("pan-to-point", utility);
    },

    onClickCategory(category) {
      this.currentCategory = category;
    },

    highlightPoint(point) {
      this.currentUtility = point;
    },

    clearCurrentCategory() {
      this.currentCategory = null;
    },

    async fetchCategories() {
      const res = await sdk.request({
        url: "/all-categories"
      });

      this.categories = res.data.data;

      this.categories.sort((a, b) => (a.name > b.name ? 1 : -1));
    },

    async fetchUtilities() {
      try {
        this.loading = true;

        this.utilities = undefined;

        const res = await sdk.request({
          url: "/utilities"
        });

        const categoryDict = this.categories.reduce((dict, c) => {
          dict[c.id] = c;

          return dict;
        }, {});

        this.utilities = res.data.data.map(u => {
          u.category = categoryDict[u.category_id];

          return u;
        });
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped>
.categories-row-container {
  display: flex;
  justify-content: flex-start;
}

.category-item {
  width: 7em;
}

.clear-current-category-button {
  position: relative;
  top: 0;
  right: 0;
  z-index: 5000;
  margin: 0;
}

.utilities-viewer-container d-flex align-center {
  position: relative;
}

/* Point Item */
.point-item-container {
  display: flex;
  align-items: center;
  border-bottom: 0.0625em solid rgba(0, 0, 0, 0.1);
}

.point-content-container {
  flex: 1;
  min-width: 0;
  padding: 0.625em 0.9375em;
}

.point-name-text {
  font-size: 1em;
  font-weight: bold;
  color: var(--text-color);
  text-transform: capitalize;
}

.point-address-text {
  color: var(--secondary-text);
  text-transform: capitalize;
}

.point-actions {
  display: flex;
  gap: 0.5em;
  padding: 0.625em 0.9375em;
}

.utilities-container {
  overflow-x: hidden;
  overflow-y: auto;
}

.add-marker-button {
  flex: none;
  margin: 0 0.625em;
}

.footer-container {
  text-align: right;
}

.show-more-less-button {
  color: var(--text-color);
  text-align: center;
  vertical-align: middle;
  font-size: 1.5em;
  line-height: 2.625em;
}
.categories-container {
  overflow-y: auto;
  flex: 1;
  min-height: 0;
}

.utilities-viewer-container {
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.utilities-container {
  overflow-x: hidden;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
}
</style>
